import { useEffect, useState } from 'react'
import { AuditLogDto } from '@ivy-monorepo/shared'
import Table from '../../components/Table'
import Pagination from '../../components/Pagination'
import { adminService } from '../../services/admin.service'
import { useSearch } from '../../contexts/SearchContext'
import { useNavigate, useSearchParams } from 'react-router-dom'

function calculateOptimalPageSize(): number {
  const viewportHeight = window.innerHeight
  const rowHeight = 56
  const headerHeight = 172
  const paginationHeight = 60

  const availableHeight = viewportHeight - headerHeight - paginationHeight
  const calculatedPageSize = Math.floor(availableHeight / rowHeight)
  return Math.max(10, Math.min(100, calculatedPageSize))
}

const AuditLogsPage = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { searchValue } = useSearch()

  const [auditLogs, setAuditLogs] = useState<AuditLogDto[]>([])
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    pageSize: 10,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  })

  const [debouncedSearchValue, setDebouncedSearchValue] = useState(searchValue)

  const currentPage = parseInt(searchParams.get('page') || '1')
  const urlPageSize = searchParams.get('pageSize')
  const currentPageSize = urlPageSize ? parseInt(urlPageSize) : calculateOptimalPageSize()
  const currentSortOrder = (searchParams.get('sortOrder') as 'asc' | 'desc') || 'desc'

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchValue(searchValue)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchValue])

  useEffect(() => {
    const fetchAuditLogs = async () => {
      try {
        const response = await adminService.getAuditLogs(
          currentPage,
          currentPageSize,
          debouncedSearchValue,
          currentSortOrder
        )
        setAuditLogs(response.auditLogs)
        setPagination(response.pagination)
      } catch (err) {
        console.log('Failed to fetch audit logs', err)
      }
    }

    fetchAuditLogs()
  }, [currentPage, currentPageSize, currentSortOrder, debouncedSearchValue, searchParams])

  const handlePageChange = (page: number) => {
    const newParams = new URLSearchParams(searchParams)
    newParams.set('page', page.toString())
    navigate(`?${newParams.toString()}`)
  }

  const handleSortChange = (_sortBy: string, sortOrder: string) => {
    const newParams = new URLSearchParams(searchParams)
    newParams.set('sortOrder', sortOrder)
    newParams.set('page', '1')
    navigate(`?${newParams.toString()}`)
  }

  return (
    <div className="users-container">
      <div className="users-header">
        <h2>Audit logs</h2>
        <Pagination currentPage={pagination.page} totalPages={pagination.totalPages} onPageChange={handlePageChange} />
      </div>

      <div className="users-table-container">
        <div className="table-wrapper">
          <Table
            id="audit-logs"
            name="Audit logs"
            keyField="publicId"
            initialSortColumn="createdAt"
            initialSortOrder={currentSortOrder}
            columns={[
              { label: 'Created', accessor: 'createdAt', type: 'datetime' },
              { label: 'Event', accessor: 'eventType', type: 'enum', enumName: 'AuditLogEventType' },
              { label: 'Producer', accessor: 'producer', type: 'enum', enumName: 'AuditLogProducer' },
              { label: 'Performed by', accessor: 'performedByFullName', type: 'text' as any },
              { label: 'Affected user', accessor: 'affectedUserFullName', type: 'text' as any },
              { label: 'Comment', accessor: 'comment', type: 'text' as any },
            ]}
            sourceData={auditLogs}
            isPropertyBarVisible={false}
            filterTerms=""
            onSortChange={handleSortChange}
            disableGlobalSearch={true}
            disableClientSort={true}
          />
        </div>
      </div>
    </div>
  )
}

export default AuditLogsPage

